import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen } from "lucide-react";
import { useBookImages } from "../hooks/useBookImages";

interface BookImageUrls {
  cover?: string | null;
  thumbnail?: string | null;
}

interface BookImageProps {
  bookId: string;
  title: string;
  className?: string;
  sizes?: string;
  imageUrls?: BookImageUrls;
  /**
   * How the artwork fills its box. "cover" crops to fill and is right for the
   * catalogue, whose covers are uniformly portrait. "contain" fits the whole
   * image and mattes the remainder — needed anywhere a generated Companion
   * cover can appear, because those are square by design (the cover engine
   * paints an S×S canvas) and cropping one into a portrait box cuts 25% of its
   * width away, including the spine strip at its left edge.
   */
  fit?: "cover" | "contain";
}

/**
 * Responsive image component for book covers with WebP/JPEG fallback and srcset
 */
export const BookImage = ({
  bookId,
  title,
  className,
  sizes,
  imageUrls,
  fit = "cover",
}: BookImageProps) => {
  const [imageError, setImageError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasProvidedImages = Boolean(imageUrls?.cover || imageUrls?.thumbnail);

  useEffect(() => {
    if (hasProvidedImages || isInView) {
      return;
    }

    const node = containerRef.current;
    if (!node) {
      return;
    }

    if (typeof window.IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasProvidedImages, isInView]);

  const shouldFetch = !hasProvidedImages && isInView;
  const { data, isLoading, isError } = useBookImages(bookId, {
    enabled: Boolean(bookId) && shouldFetch,
  });

  const containerClassName = useMemo(() => {
    const aspectAlreadySet = className?.split(/\s+/).some(value => value.startsWith("aspect"));

    return [
      "relative overflow-hidden",
      // A contained image leaves the box showing around it, so the matte has to
      // be a theme colour rather than the fixed light grey a cropped image never
      // reveals.
      fit === "contain" ? "bg-[var(--color-bg-muted)]" : "bg-gray-100",
      aspectAlreadySet ? undefined : "aspect-[2/3]",
      className,
    ]
      .filter(Boolean)
      .join(" ");
  }, [className, fit]);

  const coverUrl = imageUrls?.cover ?? data?.images?.cover ?? null;
  const thumbnailUrl = imageUrls?.thumbnail ?? data?.images?.thumbnail ?? null;
  const hasImage = !!(coverUrl || thumbnailUrl);

  if ((!hasImage && !isLoading) || imageError || isError) {
    return (
      <div ref={containerRef} className={containerClassName} role="img" aria-label={title}>
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-bg-muted)]">
          <BookOpen
            aria-hidden="true"
            strokeWidth={1.5}
            className="h-1/3 max-h-10 w-1/3 max-w-10 text-[var(--color-text-muted)]"
          />
        </div>
      </div>
    );
  }

  if (!hasImage) {
    return (
      <div ref={containerRef} className={`${containerClassName} animate-pulse`} aria-busy="true">
        <div className="absolute inset-0 bg-[var(--color-bg-muted)]" />
      </div>
    );
  }

  const srcSet = [thumbnailUrl && `${thumbnailUrl} 150w`, coverUrl && `${coverUrl} 600w`]
    .filter(Boolean)
    .join(", ");

  const fallbackSrc = (coverUrl ?? thumbnailUrl)!;

  return (
    <div ref={containerRef} className={containerClassName}>
      <picture className="absolute inset-0 block h-full w-full">
        <img
          src={fallbackSrc}
          srcSet={srcSet || undefined}
          sizes={sizes || "(max-width: 320px) 150px, (max-width: 640px) 300px, 600px"}
          alt={`Cover of ${title}`}
          className={`h-full w-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
          loading="lazy"
          onError={() => setImageError(true)}
          width={300}
          height={450}
          decoding="async"
        />
      </picture>
    </div>
  );
};
